import { useCallback, useState } from 'react';
import { Cloud as CloudIcon, ArrowRight, FileText, Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useCloud } from '@/hooks/useCloud';
import { getCloudState } from '@/lib/cloudUpsell';
import { useFileDialog } from '@/hooks/useFileDialog';
import { fetchCloudDocumentAsFile } from '@/lib/cloudApi';
import { formatBytes } from '@/lib/billing';

/**
 * CloudSourceHint — compact cloud picker that sits below a tool's drop zone.
 *
 * Two shapes based on how many cloud docs the user has:
 *
 *   count === 0 → single clickable card: "Your cloud is empty" + Upload CTA
 *                 (rare; most Pro users will already have uploaded something)
 *
 *   count  >  0 → header row + up to 3 file chips + "+N more"
 *                 Chips are one-click shortcuts — clicking opens/adds that
 *                 specific file without going through the picker modal.
 *
 * Renders null for non-cloud users so the component is safe to drop into
 * any tool page.
 *
 * Props:
 *   multiple   – boolean. Shapes the chip click: true → passes [result] to
 *                caller (adds to a list), false → passes the single result.
 *   accept     – accept filter passed to the "Browse all" modal.
 *   onPicked   – (result|results) => void. For chip clicks with multiple=true,
 *                result is always a single-item array so caller code stays
 *                identical to the Browse-all flow.
 *   className  – optional extra classes for layout.
 */
export default function CloudSourceHint({
  multiple = false,
  accept = '.pdf',
  onPicked,
  className = '',
}) {
  const { user, account } = useAuth();
  const cloudState = getCloudState(user, account);
  const { openFile, openFiles } = useFileDialog();
  const { documents, error } = useCloud({ autoLoad: cloudState === 'available' });
  const [openingId, setOpeningId] = useState(null);
  const count = Array.isArray(documents) ? documents.length : 0;

  // "Browse all" → full modal with Cloud tab preselected.
  const handleBrowse = useCallback(async () => {
    if (!onPicked) return;
    if (multiple) {
      const results = await openFiles(accept, { preferCloud: true });
      if (results && results.length > 0) onPicked(results);
    } else {
      const result = await openFile(accept, { preferCloud: true });
      if (result) onPicked(result);
    }
  }, [multiple, accept, openFile, openFiles, onPicked]);

  // Chip click → fetch + open immediately, no modal.
  const handlePickOne = useCallback(async (doc) => {
    if (!onPicked) return;
    setOpeningId(doc.id);
    try {
      const file = await fetchCloudDocumentAsFile(doc.id);
      const buffer = await file.arrayBuffer();
      const result = { name: file.name, buffer, path: null, cloudDocId: doc.id };
      onPicked(multiple ? [result] : result);
    } catch (err) {
      console.error('Pick from cloud failed:', err);
    } finally {
      setOpeningId(null);
    }
  }, [onPicked, multiple]);

  if (cloudState !== 'available') return null;

  // Offline / unreachable — don't show a misleading "empty" card that could
  // read as "your cloud was wiped". This is a secondary hint, so just hide it.
  if (count === 0 && error) return null;

  // Empty-cloud variant — compact clickable card, drops the user into Upload.
  if (count === 0) {
    return (
      <button
        type="button"
        onClick={handleBrowse}
        className={`w-full flex items-center justify-between gap-3 rounded-xl border border-brand-muted/40 bg-gradient-to-r from-brand-light to-white px-4 py-3 shadow-sm hover:shadow-md hover:border-brand-blue/50 transition-all group text-left ${className}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 text-white shadow-sm"
            style={{ background: 'linear-gradient(135deg, #4F46E5, #6366F1)' }}
          >
            <CloudIcon size={16} />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900 truncate">Your cloud is empty</div>
            <div className="text-[11px] text-gray-500 truncate">Upload a PDF to sync across devices</div>
          </div>
        </div>
        <span
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold text-white shrink-0 shadow-sm group-hover:opacity-95 transition-opacity"
          style={{ background: 'linear-gradient(135deg, #4F46E5, #6366F1)' }}
        >
          Upload
          <ArrowRight size={14} />
        </span>
      </button>
    );
  }

  // Populated variant — header + chips.
  const recent = (documents || []).slice(0, 3);
  const overflow = Math.max(0, count - recent.length);

  return (
    <div
      className={`rounded-xl border border-brand-muted/40 bg-gradient-to-r from-brand-light to-white p-3 shadow-sm ${className}`}
    >
      <div className="flex items-center justify-between gap-3 mb-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <div
            className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 text-white shadow-sm"
            style={{ background: 'linear-gradient(135deg, #4F46E5, #6366F1)' }}
          >
            <CloudIcon size={13} />
          </div>
          <span className="text-sm font-semibold text-gray-900">Your cloud</span>
          <span className="text-xs text-gray-500">
            · {count} {count === 1 ? 'file' : 'files'}
          </span>
        </div>
        <button
          type="button"
          onClick={handleBrowse}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold text-brand-blue hover:bg-white transition-colors shrink-0"
        >
          Browse all
          <ArrowRight size={12} />
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {recent.map((doc) => {
          const loading = openingId === doc.id;
          return (
            <button
              key={doc.id}
              type="button"
              onClick={() => handlePickOne(doc)}
              disabled={loading}
              title={doc.filename}
              className="group inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-white border border-gray-200 hover:border-brand-blue hover:shadow-sm transition-all text-left max-w-[280px] disabled:opacity-60"
            >
              {loading
                ? <Loader2 size={12} className="animate-spin text-brand-blue shrink-0" />
                : <FileText size={12} className="text-accent-coral shrink-0" />}
              <span className="text-xs font-medium text-gray-900 truncate">{doc.filename}</span>
              <span className="text-[10px] text-gray-400 shrink-0 tabular-nums">
                {formatBytes(doc.fileSizeBytes)}
              </span>
            </button>
          );
        })}
        {overflow > 0 && (
          <button
            type="button"
            onClick={handleBrowse}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-dashed border-gray-300 text-xs font-medium text-gray-500 hover:border-brand-blue hover:text-brand-blue transition-colors"
          >
            +{overflow} more
          </button>
        )}
      </div>
    </div>
  );
}
